<template>
  <div
    ref="contentBlockContainer"
    class="content-block-container"
    :data-external="`siging-page-content-block-page:${page.pageNum}`"
  >
    <div
      v-show="isImageLoaded"
      ref="pageRef"
      v-tooltip="{
        popperClass: 'page-tooltip hidden-xs-and-down',
        content: `${t('page')} ${page.pageNum}`,
        distance: 10,
        triggers: [],
        shown: pageTooltip,
        placement: Placement.Right,
        container: '#document-content-container',
      }"
    >
      <VideoPage
        v-if="documentPage.pageType === DocumentPageType.Video"
        :page="documentPage"
        @track-video="handleTrackVideo"
      />
      <ContentLinkPage
        v-else-if="documentPage.pageType === DocumentPageType.ContentLink"
        :content-link-block="documentPage.contentLinkBlock"
        :theme="theme"
        @click="handleContentLinkClick"
      />
      <EditorBlockView
        v-else-if="documentPage.pageType === DocumentPageType.Editor"
        :page-width="pageWidth"
        :is-preview="isPreview"
        :allowed-to-edit-after-partially-signed="allowedToEditAfterPartiallySigned"
        :recipient="recipient"
        :focused-required-id="focusedRequiredId"
        :content="editorContent"
        :block-id="editorBlockId"
        :merge-values="mergeValues"
        :context="Context.Signing"
        :theme="theme"
        :recipient-input="editorRecipientInput"
        :partially-signed-fields="partiallySignedFields"
        :sender="sender"
        :comments="filterComments(comments, editorBlockId)"
        :selected-comment-id="selectedCommentId"
        :show-contextual-comments="showContextualComments"
        :apollo-client="apolloClient"
        :create-contextual-comment-button-state="createContextualCommentButtonState"
        :show-comments-locked="showCommentsLocked"
        :all-content="allContent"
        @track-video="handleTrackVideo"
        @alert="$emit('show-alert', $event)"
        @fetch-new-image="$emit('fetch-new-image', $event)"
        @loaded="handleImageIsLoaded"
        @node-click="$emit('node-click', { ...$event, editorBlockId: documentPage.editorBlock.id })"
        @update-recipient-input="$emit('update-recipient-input', $event)"
        @partially-sign="$emit('partially-sign', $event)"
        @create-comment="$emit('create-comment', { ...$event, editorBlockId })"
        @select-comment="$emit('select-comment', $event)"
      />
      <DocumentContentPage
        v-else
        :page="page"
        :page-width="pageWidth"
        :recipient="recipient"
        :all-recipients="allRecipients"
        :sender="sender"
        :focused-required-id="focusedRequiredId"
        :hide-fields="hideFields"
        :is-signed="isSigned"
        :is-preview="isPreview"
        :required-steps-left="requiredStepsLeft"
        :initials="initials"
        :verified-initial-field-ids="verifiedInitialFieldIds"
        @update-field="handleUpdateField"
        @click-initials-field="handleClickInitialsField"
        @click-link-field="handleClickLinkField"
        @click-sign-field="handleClickSignField"
        @loaded="handleImageIsLoaded"
      />
    </div>
    <PageSkeletonLoader
      v-show="!isImageLoaded"
      class="skeleton-loader-container"
      :page-height="skeletonHeight"
    />
  </div>
</template>
<script lang="ts">
import { EditorBlockView } from '@getaccept/editor-view';
import type { MergeValues } from '@getaccept/editor-lib';
import type { PartiallySignedEvent } from '@getaccept/editor-lib/src/types/signed-field';
import type { PropType, Ref } from 'vue';
import { defineComponent, computed, ref, watch, nextTick } from 'vue';
import { useIntersectionObserver, useThrottleFn } from '@vueuse/core';
import { Placement } from '@getaccept/fried-tofu';
import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { Context } from '@getaccept/editor-lib';
import { useI18n } from 'vue-i18n';
import type { ManuallySignedPage } from '../types/manually-signed-page';
import { CreateContextualCommentButtonState } from '../contextual-commenting/types/create-contextual-comment-button-state';
import type { Recipient } from '@getaccept/lib-shared/src/types/Recipient';
import { DocumentPageType } from '@getaccept/lib-shared/src/enums/document-page-type';
import type { DocumentPage } from '@getaccept/lib-shared/src/types/document-page';
import PageSkeletonLoader from '@getaccept/lib-shared/src/skeleton-loader/components/PageSkeletonLoader.vue';
import type { EditorRecipientInput } from '@getaccept/lib-shared/src/types/editor-recipient-input';
import type { SigningTheme } from '@getaccept/lib-shared/src/signing-theme/types/theme';
import DocumentContentPage from '@getaccept/lib-shared/src/components/DocumentContentPage.vue';
import type { SigningPageUser } from '@getaccept/lib-shared/src/types/signing-page-user';
import ContentLinkPage from '@getaccept/lib-shared/src/components/ContentLinkPage.vue';
import VideoPage from '@getaccept/lib-shared/src/components/VideoPage.vue';
import type { Comment } from '@getaccept/lib-shared/src/contextual-commenting/types/comment';

export default defineComponent({
  components: {
    VideoPage,
    ContentLinkPage,
    DocumentContentPage,
    PageSkeletonLoader,
    EditorBlockView,
  },
  props: {
    page: {
      type: Object as PropType<DocumentPage | ManuallySignedPage>,
      required: true,
    },
    pages: {
      type: Array as PropType<DocumentPage[]>,
      default: () => [],
    },
    pageWidth: {
      type: Number,
      default: undefined,
    },
    recipient: {
      type: Object as PropType<Recipient>,
      default: null,
    },
    allRecipients: {
      type: Array as PropType<Recipient[]>,
      default: () => [],
    },
    sender: {
      type: Object as PropType<SigningPageUser>,
      default: undefined,
    },
    mergeValues: {
      type: Object as PropType<MergeValues>,
      default: undefined,
    },
    theme: {
      type: Object as PropType<SigningTheme>,
      default: undefined,
    },
    focusedRequiredId: {
      type: String,
      default: '',
    },
    hideFields: {
      type: Boolean,
    },
    isPreview: {
      type: Boolean,
    },
    isSigned: {
      type: Boolean,
    },
    requiredStepsLeft: {
      type: Boolean,
    },
    initials: {
      type: String,
      default: '',
    },
    verifiedInitialFieldIds: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    comments: {
      type: Array as PropType<Comment[]>,
      default: () => [],
    },
    selectedCommentId: {
      type: String,
      default: '',
    },
    showContextualComments: {
      type: Boolean,
    },
    contextualCommentingDisabled: {
      type: Boolean,
    },
    createContextualCommentButtonState: {
      type: String as PropType<CreateContextualCommentButtonState>,
      default: CreateContextualCommentButtonState.Enabled,
    },
    partiallySignedFields: {
      type: Array as PropType<PartiallySignedEvent[]>,
      default: () => [],
    },
    editorRecipientInput: {
      type: Array as PropType<EditorRecipientInput[]>,
      default: () => [],
    },
    showCommentsLocked: {
      type: Boolean,
    },
    allowedToEditAfterPartiallySigned: {
      type: Boolean,
    },
    apolloClient: {
      type: Object as PropType<ApolloClient<NormalizedCacheObject>>,
      default: () => ({}),
    },
  },
  emits: [
    'show-alert',
    'node-click',
    'update-recipient-input',
    'partially-sign',
    'create-comment',
    'select-comment',
    'click-link-field',
    'update-field',
    'click-initials-field',
    'click-sign-field',
    'load-page-thumb-url',
    'page-visibility-change',
    'track-video',
    'image-loaded',
    'content-link-click',
    'fetch-new-image',
  ],
  setup(props, { emit }) {
    const { t } = useI18n();
    const contentBlockContainer = ref(null);
    const isVisible = ref(false);
    const pageTooltip = ref(false);
    const pageRef: Ref<HTMLElement> = ref(null);

    const documentPage = computed(() => props.page as DocumentPage);

    const showPageTooltip = () => {
      if (isVisible.value) {
        pageTooltip.value = true;
        setTimeout(() => {
          pageTooltip.value = false;
        }, 2000);
      }
    };

    watch(isVisible, showPageTooltip);

    const filterComments = (comments: Comment[], editorBlockId: string): Comment[] =>
      comments.filter(comment => comment.editorSelection?.editorBlockId === editorBlockId);

    const handleUpdateField = ({ fieldId, value }: { fieldId: string; value: string }) => {
      emit('update-field', { fieldId, value });
    };

    const handleClickLinkField = (id: string) => {
      emit('click-link-field', id);
    };

    const handleClickInitialsField = (id: string) => {
      emit('click-initials-field', id);
    };

    const handleClickSignField = () => {
      emit('click-sign-field');
    };

    useIntersectionObserver(
      contentBlockContainer,
      useThrottleFn(([{ isIntersecting }]) => {
        handleVisibility(isIntersecting);
      }, 300),
      { threshold: 0.1 }
    );

    const handleVisibility = (visible: boolean) => {
      if (visible && shouldFetchThumbUrl.value) {
        emit('load-page-thumb-url', documentPage.value.id);
      }
      isVisible.value = visible;
      emit('page-visibility-change', {
        visible,
        pageId: documentPage.value.id,
        element: pageRef.value,
      });
    };

    const shouldFetchThumbUrl = computed(
      () =>
        documentPage.value.pageType === DocumentPageType.DocumentContent &&
        documentPage.value.id &&
        !documentPage.value.thumbUrl
    );

    const handleTrackVideo = (payload: { id: string; viewDuration: number }) => {
      emit('track-video', payload);
    };

    const handleImageIsLoaded = () => {
      isImageLoaded.value = true;
      nextTick(() => emit('image-loaded'));
    };

    const handleContentLinkClick = (id: string) => {
      emit('content-link-click', id);
    };

    const skeletonHeight = computed(() =>
      documentPage.value
        ? (props.pageWidth / documentPage.value.pageWidth) * documentPage.value.pageHeight
        : 900
    );

    const editorBlockId = computed(() => documentPage.value?.editorBlock?.id);

    const editorContent = computed(() => documentPage.value?.editorBlock?.content);

    const evaluateImageLoaded = computed(() =>
      [DocumentPageType.ContentLink, DocumentPageType.Video, DocumentPageType.Editor].includes(
        documentPage.value.pageType
      )
    );

    const isImageLoaded = ref(evaluateImageLoaded.value);

    const allContent = computed(() =>
      props.pages.filter(page => page.editorBlock).map(page => page.editorBlock.content)
    );

    return {
      DocumentPageType,
      filterComments,
      handleUpdateField,
      handleClickLinkField,
      handleClickInitialsField,
      handleClickSignField,
      handleVisibility,
      shouldFetchThumbUrl,
      isVisible,
      pageTooltip,
      showPageTooltip,
      handleTrackVideo,
      handleImageIsLoaded,
      handleContentLinkClick,
      skeletonHeight,
      editorBlockId,
      editorContent,
      evaluateImageLoaded,
      isImageLoaded,
      pageRef,
      contentBlockContainer,
      Placement,
      documentPage,
      Context,
      t,
      allContent,
    };
  },
});
</script>
<style lang="scss" scoped>
.content-block-container {
  position: relative;
  min-height: 1rem;
  margin-top: var(--spacing-50);
  box-shadow: var(--shadow-1);
  border-radius: var(--radius-medium);

  @media screen and (min-width: $md) {
    margin-top: var(--spacing-100);
  }
}

.skeleton-loader-container {
  background-color: var(--white);
  min-height: 80vh;
}
</style>
<style lang="scss">
.page-tooltip {
  z-index: $z-base;
}
</style>
